import { passwordResetCodeIsValid, sendPasswordResetCode } from "@/api/openapi";
import { PspContext } from "@/config/contexts";
import { translate } from "@/config/translator";
import {
  useContext,
  useState,
  useRef,
  useEffect,
  KeyboardEvent,
  Dispatch,
  SetStateAction,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

type UseCodeParams = {
  setCode: Dispatch<SetStateAction<string>>;
  code: string;
  nextStage: () => void;
};

export function useCode({ setCode, nextStage, code }: UseCodeParams) {
  const {
    state: { email },
  }: { state: { email: string } } = useLocation();
  const { lang } = useContext(PspContext);
  const navigate = useNavigate();

  const [sendCodeCooldown, setSendCodeCooldown] = useState<number | null>(null);
  const [codeWasSentMessage, setCodeWasSentMessage] = useState(false);

  const verifyButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (sendCodeCooldown === null) return;

    if (sendCodeCooldown <= 0) {
      setSendCodeCooldown(null);
      return;
    }

    const countdownTimeout = setTimeout(() => {
      setSendCodeCooldown((previousCountdown) =>
        previousCountdown === null ? null : previousCountdown - 1,
      );
    }, 1000);

    return () => clearTimeout(countdownTimeout);
  }, [sendCodeCooldown]);

  function onChangeCode(value: string) {
    const sanitizedValue = value.replace(/[^0-9-]/g, "").replace(/-+/g, "-");

    const digits = sanitizedValue.replace(/-/g, "").slice(0, 6);

    let next: string;

    if (sanitizedValue.endsWith("-") && digits.length <= 3) {
      next = digits.length === 3 ? `${digits}-` : digits;
    } else {
      next =
        digits.length <= 3
          ? digits
          : `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }
    if (next.length > 7) next = next.slice(0, 7);

    setCode(next);
  }

  function onVerify() {
    passwordResetCodeIsValid({ body: { code, email } }).then(
      ({ error, data }) => {
        if (error) {
          toast(translate("somethingWentWrong", lang));
        } else {
          if (data) {
            nextStage();
          } else {
            toast(translate("wrongResetCode", lang));
          }
        }
      },
    );
  }

  function onSendCode() {
    setSendCodeCooldown(99);
    sendPasswordResetCode({ query: { email } }).then(
      () => {
        setCodeWasSentMessage(true);
      },
      () => {
        setSendCodeCooldown(0);
      },
    );
  }

  // run once on mount
  useEffect(() => {
    onSendCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onKeyDownCodeInput(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      verifyButtonRef.current?.click();
    }
  }

  return {
    onKeyDownCodeInput,
    onSendCode,
    onVerify,
    navigate,
    lang,
    onChangeCode,
    codeWasSentMessage,
    sendCodeCooldown,
    verifyButtonRef,
    email,
  };
}
